import * as THREE from 'three';
import type { Game, Tickable } from '../engine/Game';
import { Terrain } from './Terrain';
import { Foliage } from './Foliage';
import { Water } from './Water';
import { Clouds } from './Clouds';

const skyVertex = `
  varying vec3 vWorldPos;
  void main() {
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const skyFragment = `
  varying vec3 vWorldPos;
  uniform vec3 topColor;
  uniform vec3 middleColor;
  uniform vec3 bottomColor;
  void main() {
    float h = normalize(vWorldPos).y;
    vec3 col = mix(bottomColor, middleColor, smoothstep(-0.1, 0.35, h));
    col = mix(col, topColor, smoothstep(0.35, 0.85, h));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class World implements Tickable {
  readonly terrain: Terrain;
  readonly foliage: Foliage;
  readonly water: Water;
  readonly clouds: Clouds;
  private sunLight: THREE.DirectionalLight;

  constructor(game: Game) {
    const scene = game.scene;
    scene.background = new THREE.Color('#f2e0ff');
    scene.fog = new THREE.Fog('#f2e0ff', 70, 160);

    const ambient = new THREE.HemisphereLight('#ffd3ea', '#cfe4c7', 0.75);
    scene.add(ambient);

    const sun = new THREE.DirectionalLight('#fff4d0', 1.2);
    sun.position.set(30, 50, 20);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 120;
    sun.shadow.camera.left = -60;
    sun.shadow.camera.right = 60;
    sun.shadow.camera.top = 60;
    sun.shadow.camera.bottom = -60;
    sun.shadow.bias = -0.0005;
    scene.add(sun);
    this.sunLight = sun;

    // Sky dome.
    const skyMat = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color('#aed4ff') },
        middleColor: { value: new THREE.Color('#ffd7e8') },
        bottomColor: { value: new THREE.Color('#fff0d7') },
      },
      vertexShader: skyVertex,
      fragmentShader: skyFragment,
      side: THREE.BackSide,
      depthWrite: false,
    });
    const sky = new THREE.Mesh(new THREE.SphereGeometry(240, 32, 16), skyMat);
    scene.add(sky);

    this.terrain = new Terrain();
    scene.add(this.terrain.mesh);

    this.foliage = new Foliage(this.terrain);
    scene.add(this.foliage.group);

    this.water = new Water(this.terrain);
    scene.add(this.water.mesh);

    this.clouds = new Clouds();
    scene.add(this.clouds.group);
  }

  tick(dt: number, elapsed: number): void {
    this.water.tick(dt, elapsed);
    this.clouds.tick(dt);
  }
}
